import React, {useState} from 'react';
import {StorageObject} from '../../../models/StorageObject';
import {BreadcrumbsDTO} from '../../../models/dto/BreadcrumbsDTO';
import {TabButton, TabNavigation, TabNavigationWrapper} from './FolderMetaDataTabView.style';
import {getTab} from '../../../utils/TabUtils';
import {FolderMetaDataTabMain} from './FolderMetaDataTabMain';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {MetaDataTab} from '../MetaDataTab';
import {FolderMetaDataTabAdditional} from './FolderMetaDataTabAdditional';
import {FolderMetaDataTabPermissions} from './FolderMetaDataTabPermissions';
import {FolderMetaDataTabLocales} from './FolderMetaDataTabLocales';
import EventEmitter from 'events';
import {Label} from '../../../models/Label';
import {t} from 'i18next';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_NAME,
    STORAGE_OBJECT_OBJECT_IS_PUBLIC,
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_COMMENT,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO,
    STORAGE_OBJECT_OBJECT_METADATA_FILE_COUNT,
    STORAGE_OBJECT_OBJECT_METADATA_FOLDER_COUNT,
    STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_ROLES,
    STORAGE_OBJECT_OBJECT_METADATA_TAGS,
    STORAGE_OBJECT_OBJECT_METADATA_VERSION,
    STORAGE_OBJECT_TOTAL_SIZE
} from '../../../init/storage_object_constants';

export interface FolderMetaDataTabViewProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly item: StorageObject;
    readonly breadcrumbs: BreadcrumbsDTO[];
    readonly currentPath: string;
    readonly rolesDataSource: string[];
    readonly parentOwnerRoles: string[];
    readonly parentRoles: string[];
    readonly parentLocales: string[];
    readonly language: string;
    readonly defaultLocale: string;
    readonly labelsInitialState: Map<string, Label>;
    readonly versionInitialState: string;
    readonly eventEmitter: EventEmitter;
}

const getTabs = (): string[] => {
    let tabs: string[] = [MetaDataTab.MAIN, MetaDataTab.ADDITIONAL, MetaDataTab.PERMISSIONS, MetaDataTab.LOCALES];

    return tabs;
}

/**
 * @name FolderMetaDataTabView
 * @description
 * The dialog to add the metadata of a newly created folder or edited folder
 */
export const FolderMetaDataTabView: React.VFC<FolderMetaDataTabViewProps> = (props) => {
    const getApplicableTabs = (): string[] => {
        return getTabs().filter(tab =>
            (((MetaDataTab.MAIN === tab)
                    && (isVisibleComponent(STORAGE_OBJECT_NAME, props.templatePropertySettings)
                        || isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, props.templatePropertySettings)
                        || ((isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)
                                && (isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, props.templatePropertySettings)))
                            || (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                                && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_VERSION, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_COMMENT, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_FOLDER_COUNT, props.templatePropertySettings)
                                    || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_FILE_COUNT, props.templatePropertySettings))))))
                || ((MetaDataTab.ADDITIONAL === tab)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, props.templatePropertySettings))))
                || ((MetaDataTab.PERMISSIONS === tab)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, props.templatePropertySettings)
                        || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                        && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_ROLES, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_ROLES, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_PARENT_OWNER_ROLES, props.templatePropertySettings)
                            || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_OWNER_ROLES, props.templatePropertySettings))))
                || ((MetaDataTab.LOCALES === tab)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)))
        ) ?? [] as string[];
    }

    const [tabs] = useState(getApplicableTabs());
    const [activeTab, setActiveTab] = useState(getTab(MetaDataTab.MAIN, getApplicableTabs()));

    const handleTabClick = (tab: React.SetStateAction<string>) => {
        setActiveTab(tab);
    };

    return (
        (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
            || isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, props.templatePropertySettings)) ?
            <TabNavigationWrapper id={generateIdentifier()} className='a-tab-navigation__wrapper'>
                <div id={generateIdentifier()} className='a-tab-navigation__gradients'>
                    <TabNavigation id={generateIdentifier()} className='a-tab-navigation' role='tablist'>
                        {tabs?.map((tab, index) => (
                            <li id={generateIdentifier(`'data-tab-item-'${index}`, '-')}
                                key={`'data-tab-item-'${index}`}
                                className='a-tab-navigation__item'
                                role='none'>
                                <TabButton id={generateIdentifier(`'data-tab-'${tab}`, '-')}
                                           type='button'
                                           className={`a-tab-navigation__tab ${activeTab === tab ? '-selected' : ''}`}
                                           data-frok-tab-identifier={`${index + 1}`}
                                           tabIndex={index}
                                           role='tab'
                                           aria-controls={`'data-tab-'${tab}`}
                                           onClick={() => handleTabClick(tab)}>
                                    <span id={generateIdentifier()} className='a-tab-navigation__tab-content'>
                                        <span id={generateIdentifier()}
                                              className='a-tab-navigation__label'>{t(tab)}</span>
                                    </span>
                                </TabButton>
                            </li>
                        ))}
                    </TabNavigation>
                    {(MetaDataTab.MAIN === activeTab) &&
                        <FolderMetaDataTabMain templatePropertySettings={props.templatePropertySettings}
                                               item={props.item}
                                               parentLocales={props.parentLocales}
                                               language={props.language}
                                               defaultLocale={props.defaultLocale}
                                               labelsInitialState={props.labelsInitialState}
                                               versionInitialState={props.versionInitialState}
                                               eventEmitter={props.eventEmitter}/>
                    }
                    {(MetaDataTab.ADDITIONAL === activeTab) &&
                        <FolderMetaDataTabAdditional templatePropertySettings={props.templatePropertySettings}
                                                     dateValidFrom={props.item.metadata.dateValidFrom}
                                                     dateValidTo={props.item.metadata.dateValidTo}
                                                     tags={props.item.metadata.tags}
                                                     eventEmitter={props.eventEmitter}/>
                    }
                    {(MetaDataTab.PERMISSIONS === activeTab) &&
                        <FolderMetaDataTabPermissions templatePropertySettings={props.templatePropertySettings}
                                                      isPublic={props.item.isPublic}
                                                      ownerRoles={props.item.metadata.ownerRoles}
                                                      roles={props.item.metadata.roles}
                                                      parentOwnerRoles={props.parentOwnerRoles}
                                                      parentRoles={props.parentRoles}
                                                      rolesDataSource={props.rolesDataSource}
                                                      eventEmitter={props.eventEmitter}/>
                    }
                    {(MetaDataTab.LOCALES === activeTab) &&
                        <FolderMetaDataTabLocales templatePropertySettings={props.templatePropertySettings}
                                                  labels={props.item.labels}
                                                  parentLocales={props.parentLocales}
                                                  defaultLocale={props.defaultLocale}
                                                  labelsInitialState={props.labelsInitialState}
                                                  eventEmitter={props.eventEmitter}/>
                    }
                </div>
            </TabNavigationWrapper> :
            <TabNavigationWrapper id={generateIdentifier()} className='a-tab-navigation__wrapper'/>
    );
}